/**
 * Sortieren durch Klick auf den Spaltenkopf.
 *
 * **Die erste sortierbare Tabelle des Programms** — und deshalb steht die
 * Regel hier und nicht in dem Bildschirm, der sie zuerst braucht. Die zweite
 * Tabelle soll sie erben, nicht nacherfinden: Zwei Tabellen, die sich beim
 * dritten Klick verschieden verhalten, sind schlimmer als eine, die gar nicht
 * sortiert.
 *
 * **Eine Regel, kein Sonderfall je Spalte:**
 *
 * > Erster Klick aufsteigend · zweiter absteigend · dritter zurück zur
 * > Reihenfolge der Liste selbst.
 *
 * Die Versuchung, bei Beträgen und Perioden mit *absteigend* anzufangen, ist
 * groß — das will man dort meistens. Sie ist trotzdem abgelehnt: Eine Regel,
 * die je nach Spalte anders anfängt, kann sich niemand merken, und der
 * Anwender klickt dann zweimal und sieht nach, statt einmal zu klicken und zu
 * wissen. **Der dritte Zustand ist der wichtigere Gewinn:** Er bringt die
 * Liste in ihre eigene Ordnung zurück, und die trägt bei einem Belegordner
 * eine Aussage — die Reihenfolge, in der gebucht wurde.
 *
 * **Nicht sortiert wird ein Bericht.** Eine Bilanz steht in der Reihenfolge
 * des Positionsplans; nach Betrag sortiert wäre sie keine Bilanz mehr,
 * sondern eine Rangliste. Sortierbar sind Listen — Belege, Treffer, Anwender.
 */

export type Richtung = 'auf' | 'ab'

/** Wonach gerade sortiert wird — <c>null</c> heißt: in der Ordnung der Liste. */
export type Sortierwahl = { spalte: string; richtung: Richtung } | null

/**
 * Was ein Klick auf einen Spaltenkopf aus der bisherigen Wahl macht.
 *
 * Ein Klick auf eine **andere** Spalte fängt dort wieder bei aufsteigend an —
 * er setzt nicht die Richtung der vorigen Spalte fort. Sonst hinge die
 * Wirkung eines Klicks davon ab, was vorher angeklickt war.
 */
export function naechste(jetzt: Sortierwahl, spalte: string): Sortierwahl {
  if (jetzt === null || jetzt.spalte !== spalte) {
    return { spalte, richtung: 'auf' }
  }

  return jetzt.richtung === 'auf' ? { spalte, richtung: 'ab' } : null
}

/**
 * Was eine Zelle zum Vergleichen hergibt.
 *
 * `null` heißt **leer** und wird immer ans Ende sortiert — in beiden
 * Richtungen. Eine Liste, in der die leeren Zeilen beim Umdrehen nach oben
 * springen, liest sich, als wären sie die größten.
 */
export type Schluessel = string | number | null

/**
 * Sortiert eine Kopie der Liste.
 *
 * **Stabil**, weil `Array.prototype.sort` es seit ES2019 ist: Zeilen mit
 * demselben Schlüssel behalten die Reihenfolge, in der sie ankamen — bei
 * einem Belegordner also die Buchungsreihenfolge. Ohne das wechselten gleich
 * bewertete Zeilen bei jedem Klick die Plätze.
 */
export function sortiere<T>(
  zeilen: readonly T[],
  wahl: Sortierwahl,
  schluessel: (zeile: T, spalte: string) => Schluessel,
): T[] {
  if (wahl === null) {
    return [...zeilen]
  }

  const vorzeichen = wahl.richtung === 'auf' ? 1 : -1

  return [...zeilen].sort((a, b) => {
    const x = schluessel(a, wahl.spalte)
    const y = schluessel(b, wahl.spalte)

    // Leeres steht am Ende, gleich in welcher Richtung.
    if (x === null && y === null) return 0
    if (x === null) return 1
    if (y === null) return -1

    if (typeof x === 'number' && typeof y === 'number') {
      return (x - y) * vorzeichen
    }

    // **Nach Landessprache vergleichen, nicht nach Zeichencode.** Sonst steht
    // „Überleitung" hinter „Zugang", und ein deutscher Kontenrahmen sortiert
    // sich in einer Reihenfolge, die kein Anwender erwartet.
    return String(x).localeCompare(String(y), undefined, { numeric: true }) * vorzeichen
  })
}

/** Was `aria-sort` an einem Spaltenkopf tragen muss (WAI-ARIA). */
export function ariaSortierung(wahl: Sortierwahl, spalte: string): 'ascending' | 'descending' | 'none' {
  if (wahl?.spalte !== spalte) {
    return 'none'
  }

  return wahl.richtung === 'auf' ? 'ascending' : 'descending'
}
